import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { TabContext, TabList } from '@mui/lab';
import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Resources = () => {
  const [tabvalue, setTabValue] = useState<any>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            switch (entry.target.id) {
              case 'excise':
                setTabValue(1);
                break;
              case 'businesstax':
                setTabValue(7);
                break;
              case 'exempt':
                setTabValue(2);
                break;
              case 'employment':
                setTabValue(3);
                break;
              case 'extension':
                setTabValue(5);
                break;
              case 'informationreturns':
                setTabValue(4);
                break;
              case 'essential':
                setTabValue(6);
                break;
            }
          }
        });
      },
      {
        rootMargin: '-30% 0px -60% 0px',
      }
    );

    const sectionIds = [
      'excise',
      'businesstax',
      'exempt',
      'employment',
      'extension',
      'informationreturns',
      'essential',
    ];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const terms = [
      'Search — Form 2290',
      'Search — Form 941',
      'Search — Form W-2',
      'Search — Form 1120-S',
      'Search — Form 990-EZ',
      'Search — Form 1099',
      'Search — Form 7004',
      'Search — Schedule M-3',
      'Search — Schedule D',
      'Search — Form 1042-S',
    ];

    const input = searchInputRef.current;
    if (!input) return;

    let termIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timeoutId: NodeJS.Timeout;

    const TYPE_SPEED = 55;
    const DELETE_SPEED = 30;
    const PAUSE_AFTER_TYPE = 1400;
    const PAUSE_AFTER_DELETE = 300;

    const tick = () => {
      const full = terms[termIndex];
      let displayed;

      if (!deleting) {
        charIndex += 1;
        displayed = full?.slice(0, charIndex);
        input.placeholder = displayed + '|';

        if (charIndex === full?.length) {
          deleting = true;
          timeoutId = setTimeout(tick, PAUSE_AFTER_TYPE);
          return;
        }
        timeoutId = setTimeout(tick, TYPE_SPEED);
      } else {
        const staticPrefix = 'Search — ';
        charIndex -= 1;
        if (charIndex < staticPrefix.length) {
          deleting = false;
          termIndex = (termIndex + 1) % terms.length;
          charIndex = staticPrefix.length;
          timeoutId = setTimeout(tick, PAUSE_AFTER_DELETE);
          return;
        }
        displayed = full?.slice(0, charIndex);
        input.placeholder = displayed + '|';
        timeoutId = setTimeout(tick, DELETE_SPEED);
      }
    };

    const staticPrefix = 'Search — ';
    charIndex = staticPrefix.length;
    input.placeholder = staticPrefix + '|';
    timeoutId = setTimeout(tick, PAUSE_AFTER_DELETE);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    setSearchQuery('');
  };

  useEffect(() => {
    // Merge continuation lists to fix spacing
    const gridContainers = document.querySelectorAll('.MuiGrid-root-width');
    gridContainers.forEach((container) => {
      let currentActiveUl: HTMLUListElement | null = null;

      const gridItems = Array.from(container.children);
      gridItems.forEach((gridItem) => {
        // Skip the very first Box which is the category header (e.g. Excise Tax Forms),
        // we are looking for the sub-grids. Wait, gridItems ARE the sub-grids!
        const headerBox = gridItem.querySelector(
          ':scope > .MuiBox-root:first-child'
        );
        const ul = gridItem.querySelector('ul');

        if (headerBox && !headerBox.textContent?.trim()) {
          if (currentActiveUl && ul) {
            // Move all <li> to the active <ul>
            const lis = Array.from(ul.children);
            lis.forEach((li) => currentActiveUl!.appendChild(li));
            // Hide this empty grid item entirely
            const gridItemElement = gridItem as HTMLElement;
            gridItemElement.style.display = 'none';
          }
        } else {
          if (ul) {
            currentActiveUl = ul;
          }
        }
      });
    });
  }, []);

  useEffect(() => {
    const term = searchQuery.toLowerCase();
    const categoryIds = [
      'excise',
      'businesstax',
      'exempt',
      'employment',
      'extension',
      'informationreturns',
      'essential',
    ];

    let firstMatchedId: string | null = null;

    categoryIds.forEach((id) => {
      const catEl = document.getElementById(id);
      if (!catEl) return;

      let hasVisibleItem = false;
      const uls = catEl.querySelectorAll('ul');

      uls.forEach((ul) => {
        let ulHasVisibleItem = false;
        const lis = ul.querySelectorAll('li');

        if (lis.length === 0) return; // Skip empty lists (merged away)

        lis.forEach((li) => {
          if (!term || li.textContent?.toLowerCase().includes(term)) {
            const liElement = li as HTMLElement;
            liElement.style.display = '';
            ulHasVisibleItem = true;
          } else {
            const liElement = li as HTMLElement;
            liElement.style.display = 'none';
          }
        });

        const gridParent = ul.parentElement;
        if (gridParent) {
          if (!term) {
            gridParent.style.display = '';
          } else {
            gridParent.style.display = ulHasVisibleItem ? '' : 'none';
          }
        }

        if (ulHasVisibleItem) hasVisibleItem = true;
      });

      if (!term) {
        catEl.style.display = '';
      } else {
        catEl.style.display = hasVisibleItem ? 'flex' : 'none';
        if (hasVisibleItem && !firstMatchedId) {
          firstMatchedId = id;
        }
      }
    });

    // Update tab selection based on search match or clear
    if (!term) {
      setTabValue(7); // Default to Excise Tax Forms when cleared
    } else if (firstMatchedId) {
      const tabMap: Record<string, number> = {
        excise: 1,
        businesstax: 7,
        exempt: 2,
        employment: 3,
        extension: 5,
        informationreturns: 4,
        essential: 6,
      };
      setTabValue(tabMap[firstMatchedId]);
    }
  }, [searchQuery]);
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Tax Forms, Instructions & Filing Resource Center"
          description="Access IRS tax form resources with instructions, due dates, and compliance for Forms 941, 2290, 990, W-2, 1099, 7004, 4868, 8868, and more."
          keywords=""
          canonical="resources/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ textAlign: 'center', pb: 2, pt: 1 }}
        component="section"
      >
        <header>
          <Typography
            variant="h1"
            sx={{
              fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
              fontWeight: 600,
              mt: 3,
            }}
          >
            <span className="highlight-container">
              <span className="highlight"> Resource Center</span>
            </span>{' '}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
              fontWeight: 400,
              mt: 1,
              mb: 2,
            }}
          >
            Find all tax form resources in one place — including form
            instructions,
            <br /> and due dates — to help you stay informed and compliant.
          </Typography>
        </header>
        <Box sx={{ maxWidth: 480, mx: 'auto', mb: 2 }}>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            variant="outlined"
            placeholder="Search — "
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: 2, bgcolor: '#fff' },
            }}
            size="small"
          />
        </Box>
      </Container>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          pb: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
        component="section"
      >
        <Box
          sx={{
            // background: 'rgba(255,255, 255, .85)',
            backdropFilter: 'saturate(50%) blur(5px)',
            position: 'sticky',
            top: 60,
            zIndex: 9,
            width: '100%',
          }}
        >
          <Container maxWidth="xl" sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
            <TabContext value={tabvalue}>
              <TabList
                variant="scrollable"
                onChange={handleTabChange}
                scrollButtons
                allowScrollButtonsMobile
                aria-label="price tabs"
                sx={{
                  maxWidth: 1275,
                  mx: 'auto',
                  alignItems: 'center',
                  '& .MuiTabs-flexContainer .MuiButtonBase-root': {
                    textTransform: 'none',
                    color: '#637381',
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    py: 0.75,
                    px: 1.5,
                    mx: 0.5,
                    minHeight: 'inherit',
                    transition: 'all 0.3s ease 0s',
                    mt: 1,
                  },
                  '& .MuiTabs-flexContainer .MuiButtonBase-root:hover': {
                    borderColor: '#213e99',
                    backgroundColor: 'rgba(33, 62, 153, 0.10)',
                  },
                  '& .MuiTabs-scroller': { minHeight: 48 },
                  '& .MuiTabs-indicator': { height: 5 },
                  '& .MuiTab-root.Mui-selected': {
                    color: '#fff !important',
                    borderColor: '#213e99 !important',
                    backgroundColor: '#213e99 !important',
                  },
                }}
              >
                <Tab value={7} label="Business Tax Forms" href="#businesstax" />{' '}
                <Tab
                  value={3}
                  label={<React.Fragment>Employment Tax Forms</React.Fragment>}
                  href="#employment"
                />
                <Tab value={2} label="Exempt Org. Forms" href="#exempt" />
                <Tab
                  value={4}
                  label={<React.Fragment>Information Returns</React.Fragment>}
                  href="#informationreturns"
                />
                <Tab value={1} label="Excise Tax Forms" href="#excise" />
                <Tab value={5} label="Extension Forms" href="#extension" />
                <Tab value={6} label="Essential Forms" href="#essential" />
              </TabList>
            </TabContext>
          </Container>
        </Box>
        <Container
          maxWidth="xl"
          sx={{
            '& ul': {
              pl: 0,
              listStyleType: 'none',
              display: 'grid',
              gap: 0.75,
              mt: 1,
              mb: 0,
              gridTemplateColumns: '1fr',
              '@media (min-width: 600px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              '@media (min-width: 900px)': {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            },
            '& li': { mb: '0 !important', width: '100%' },
            '& li a': {
              py: 1,
              px: 2,
              height: '100%',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s ease 0s',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              '&:hover': { background: 'rgba(0, 0, 0, 0.1)' },
            },
            '& li a img': { mb: '0 !important' },
          }}
        >
          <Box
            className="card"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#e8e1f5',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="businesstax"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/business-icon-app.webp" />
                    <source srcSet="/assets/images/business-icon-app.png" />
                    <img
                      src="/assets/images/business-icon-app.png"
                      alt="Business Tax Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Business Tax</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    1120-S, 1065,
                  </Typography>
                </Box>
              </Stack>

              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="Business Tax Extension "
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Guide for S Corporations
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        1120-S
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-100s-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 100S Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-filing-deadlines-and-penalties-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S Mailing Address
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-k-1/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule K-1 (Form 1120-S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120s-schedule-d/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule D (Form 1120-S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-b/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule B (Form 1120-S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-m3-form-1120s/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule M-3 (Form 1120-S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-k/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule K (Form 1120-S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-l/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule L (Form 1120‑S)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-m-1/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Schedule M‑1
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-m-2/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Schedule M‑2
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-k-vs-schedule-k-1/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule K vs Schedule K-1
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120s-vs-form-1120-comparison/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S vs. Form 1120
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-separately-stated-income-items-of-an-s-corporation/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S Separated Stated Income Items
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-separately-vs-nonseparately-stated-items/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Separately vs Nonseparately Stated Items
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120-s-stock-and-debt-basis/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S Stock and Debt Basis
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="Business Tax Extension"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Guide for Partnership
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        1065
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/business-tax/form-1065-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1065 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1065-filing-deadlines-and-penalties-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1065 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1065-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1065 Mailing Address
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-k1-1065/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule K-1 (Form 1065)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-m-3-form-1065/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1065 Schedule M-3
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/business-tax/schedule-m-1-form-1065/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1065 Schedule M-1
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-b-2-form-1065-instruction/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule B-1 (Form 1065)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/schedule-b-2-form-1065-instruction/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule B-2 (Form 1065)
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Related Forms & Resources"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Related Forms & Resources
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/business-tax/form-1125-a/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Form 1125-A
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1125-e/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Form 1125-E
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/business-tax/form-8825/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Form 8825
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/business-tax/form-4562/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4562
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-4797/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRS Form 4797
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-7205/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7205
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/business-tax/form-1120s-vs-form-1065-comparison/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-S vs. Form 1065
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Business tax Forms End */}
          {/* Employment Tax Forms Start */}
          <Box
            className="card"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#ddf3f6',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="employment"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/employment-icon.webp" />
                    <source srcSet="/assets/images/employment-icon.png" />
                    <img
                      src="/assets/images/employment-icon.png"
                      alt="Employment Tax Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Employment Tax</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    941, 941-X, 941 Schedule R, 940, 943, 943-X, 944, 945,
                    945-X, 94X PIN Request
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="getting started"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Getting Started with Form 941{' '}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        {' '}
                        (Quarterly Form)
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/irs-form-941-2024-updates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Revised Form 941 2024
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-instructions-2023/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Instruction 2023
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Mailing Address
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-signature-options/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Signature Options
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/schedule-icon.svg"
                      loading="lazy"
                      alt="Form 941 Schedules"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Form 941 Schedules
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        {' '}
                        & Amendments
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Schedule B
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-schedule-r/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Schedule R
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-bulk-upload/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Bulk Upload
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-x-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941-X Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-x-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941-X Due Dates
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/penalties-icon.svg"
                      loading="lazy"
                      alt="Penalties & Compliance"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Penalties & Compliance
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 Penalties
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-944-deposit-requirements/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Payroll Tax Deposits
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/other-icon.svg"
                      loading="lazy"
                      alt="Penalties & Compliance"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Other Forms
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/employment-tax/employer-payroll-taxes/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Employer Payroll Taxes
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/form-8974-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8974 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/employers-quarterly-federal-tax-return/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Quarterly Federal Tax Return
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Penalties & Compliance"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Form Comparison & Tax Concepts
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/employment-tax/form-941-vs-form-944/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 941 vs Form 944
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/futa-tax/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FUTA
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/employment-tax/fica-tax/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FICA Tax
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/other-icon.svg"
                      loading="lazy"
                      alt="Other Employment Tax Forms"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Other Employment Tax Forms
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        {' '}
                        (Annual Forms)
                      </Typography>
                    </Box>
                  </Box>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 16, sm: 15, xs: 15 },
                        mt: -1,
                      }}
                    >
                      Form 940 (FUTA Tax)
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyleType: 'none',
                        // '& li': { mb: 1.2 },
                      }}
                    >
                      <li>
                        <Link
                          href="/resources/employment-tax/form-940-instructions/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 940 Instructions
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/irs-form-940-due-dates/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 940 Due Dates
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-940-mailing-address/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 940 Mailing Address
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-940-schedule-a/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 940 Schedule A
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-940-vs-form-941/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 940 VS Form 941
                        </Link>
                      </li>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {' '}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 16, sm: 15, xs: 15 },
                        mt: 1,
                      }}
                    >
                      Form 943 (Agricultural Employers)
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyleType: 'none',
                        // '& li': { mb: 1.2 },
                      }}
                    >
                      <li>
                        <Link
                          href="/resources/employment-tax/form-943-instructions/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 943 Instructions
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-943-due-dates/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 943 Due Dates
                        </Link>
                      </li>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {' '}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 16, sm: 15, xs: 15 },
                        mt: 1,
                      }}
                    >
                      Form 944 (Employer Tax Return)
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyleType: 'none',
                        // '& li': { mb: 1.2 },
                      }}
                    >
                      <li>
                        <Link
                          href="/resources/employment-tax/form-944-instructions/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 944 Instructions
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-944-due-dates/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 944 Due Dates
                        </Link>
                      </li>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {' '}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 16, sm: 15, xs: 15 },
                        mt: 1,
                      }}
                    >
                      Form 945 (Withheld Federal Income Tax)
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyleType: 'none',
                        // '& li': { mb: 1.2 },
                      }}
                    >
                      <li>
                        <Link
                          href="/resources/employment-tax/form-945-instructions/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 945 Instructions
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-945-due-dates/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 945 Due Dates
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/resources/employment-tax/form-945-x-instructions/"
                          sx={{
                            textDecoration: 'none',
                            color: '#333',
                            '&:hover': { color: '#0202ff' },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/images/form-icon.svg"
                            loading="lazy"
                            sx={{ mr: 1, mb: -0.5 }}
                          />
                          Form 945-X Instructions
                        </Link>
                      </li>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Employment Tax Forms End */}

          {/* Exempt Org. Forms Start */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#f6e6ea',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="exempt"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/nonprofit-icon.webp" />
                    <source srcSet="/assets/images/nonprofit-icon.png" />
                    <img
                      src="/assets/images/nonprofit-icon.png"
                      alt="Exempt Organizations Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Exempt Org.</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    990-N, 990-EZ, 990, 990-PF, 990-T, 5227, CA 199, 1120-POL,
                    CA 109,
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="getting started"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Getting Started with Form 990
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-ez-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990-EZ Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-n-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990-N Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-pf-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990-PF Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-t-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990-T Instructions
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/tax-exempt/ftb-ca-form-199-filing/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FTB CA Form 199 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-1023/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1023
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-5227-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 5227 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/501c3-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 501c3 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-1120-pol-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-POL Instructions
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/tax-exempt/ftb-form-3885f-depreciation/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FTB Form 3885F
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/ftb-form-3885/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FTB Form 3885
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-ftb-3509-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        FTB Form 3509
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/schedule-icon.svg"
                      loading="lazy"
                      alt="Deadlines & Mailing"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Deadlines & Mailing
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    & Amendments
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        990 Forms Series Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990 Mailing Address
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-5227-filing-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 5227 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-1120-pol-filing-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1120-POL Due Date
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/penalties-icon.svg"
                      loading="lazy"
                      alt="Compliance "
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Compliance
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Forms 990 Penalties
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Automatic Revocation
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-5227-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 5227 Penalties
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/transfer-election-statement/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Transfer Election Statement
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/ca-form-rrf1-filing-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form RRF-1
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form Comparisons"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Form Comparisons
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-vs-form-990-ez/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990 vs Form 990-EZ
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-n-vs-form-990-ez/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 990-N vs Form 990-EZ
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Other Employment Tax Forms"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Form 5227 Schedules
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Auto-Generated when you file with us)
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-5227-schedule-e/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 5227 Schedule E (1040)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8582-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8582 Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Other Employment Tax Forms"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        CA FTB Form 109
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Instructions, Schedules & Related Forms)
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-109-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        CA Form 109 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/schedule-d-541/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule D (541)
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form 990 Schedules"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Form 990, 990-EZ, or 990-PF Schedules
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        {' '}
                        (Auto-Generated when you file with us)
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-a/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule A
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-b/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule B
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-c/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule C
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-d/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule D
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-e/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule E
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-f/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule F
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-g/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule G
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-i/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule I
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-j/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule J
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-k/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule K
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-l/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule L
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-m/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule M
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-n/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule N
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-o/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule O
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-r/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule R
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ ml: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Other Employment Tax Forms"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Form 990-T Schedules
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Auto-Generated when you file with us)
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      // '& li': { mb: 1.2 },
                    }}
                  >
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-990-t-schedule-a/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        990-T Schedule A
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/schedule-d-form-1120-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1120 Schedule D
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/schedule-d-form-1041-resource-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1041 Schedule D
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/1041-schedule-i-amt/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1041 Schedule I
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-4797-sales-business-property/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4797
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-4562-depreciation-amortization-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4562
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8050-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8050
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-3800-schedule-a/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Schedule A (Form 3800)
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8949
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8995-qualified-business-income-deduction/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8995
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-2220-underpayment-estimated-tax-corporations/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2220
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-3800-business-credits/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3800
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-3468-investment-credit/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3468
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8911-alternative-fuel-credits/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8911
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-exempt/form-8936-clean-vehicle-credit/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8936
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Exempt Org. Forms End */}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#dae6f6',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="informationreturns"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/information-icon.webp" />
                    <source srcSet="/assets/images/information-icon.png" />
                    <img
                      src="/assets/images/information-icon.png"
                      alt="Information Returns"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Information</span>
                    </span>{' '}
                    Returns
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    W-2, 1099, W-2G, 1097, 5498, 3921, 3922, 1098, and 1095
                    Forms
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form W-2 Series"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Form W-2 Series
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Wage and Tax Statements)
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/2024-form-w2-updates-filing-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        2024 Form W-2 Updates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/2025-form-w2-updates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        2025 Form W-2 Updates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w2-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        W-2 Penalties for 2025
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w2-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2 Mailing Address
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/schedule-icon.svg"
                  loading="lazy"
                  alt="Form 941 Schedules"
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 2,
                        }}
                      >
                        {/* Payment Options & Mobile App */}
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    & Amendments
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2c-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2c Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w2-vs-w4/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2 vs Form W-4
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w2-vs-w9/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2 vs Form W-9
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form W-2 Series"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Form W-2G
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Certain Gambling Winnings)
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2g-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2G Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2g-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-2G Due Dates
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      fontSize: { md: 18, sm: 18, xs: 17 },
                      mt: 1,
                    }}
                  >
                    1099 Series
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    (Information Returns)
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: -1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Guides for Filers"
                    /> */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 16, sm: 16, xs: 15 },
                          mt: 0,
                        }}
                      >
                        Common 1099 Forms
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-nec-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-NEC Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-misc-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-MISC Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-k-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-K Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-g-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-G Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Guides for Filers"
                    /> */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 16, sm: 16, xs: 15 },
                          mt: 0,
                        }}
                      >
                        1099 Due Dates
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099 Series Due Dates
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Guides for Filers"
                    /> */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 16, sm: 16, xs: 15 },
                          mt: 0,
                        }}
                      >
                        Investment & Retirement-Related 1099s
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-int-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-INT Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-div-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-DIV Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-b-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-B Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-oid-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-OID Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-patr-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-PATR Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Digital Assets"
                    /> */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 16, sm: 16, xs: 15 },
                          mt: 0,
                        }}
                      >
                        Digital Assets
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-da-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-DA Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Guides for Filers"
                    /> */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 16, sm: 16, xs: 15 },
                          mt: 0,
                        }}
                      >
                        Debt, Property, and Other Distributions
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-a-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-A Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-c-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-C Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-r-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-R Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-s-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-S Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-q-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-Q Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-sa-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-SA Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-ltc-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1099-LTC Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form 1098 Series"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        1098 Series
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Mortgage and Tuition Statements)
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1098 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-e-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1098-E Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-t-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1098-T Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/schedule-icon.svg"
                  loading="lazy"
                  alt="Form 941 Schedules"
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 2,
                        }}
                      >
                        {/* Payment Options & Mobile App */}
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    & Amendments
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-c-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-C Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-f-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-F Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-q-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-Q Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/penalties-icon.svg"
                  loading="lazy"
                  alt="Penalties, Credits & Amendments"
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 2,
                        }}
                      >
                        {/* Penalties, Credits & Amendments */}
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-series-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1098 Series Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-c-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-C Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-f-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-F Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1098-q-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1098-Q Due Dates
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form 5498 Series"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        5498 Series
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (IRA & Savings Contributions)
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-esa-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498-ESA Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-sa-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498-SA Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498 Due Dates
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-esa-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498-ESA Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-sa-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498-SA Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-5498-qa-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        5498-QA Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Form ACA Series"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        ACA Forms
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        (Affordable Care Act Reporting)
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1095-b-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1095-B Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1095-c-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        1095-C Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/aca-form-due-date/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        ACA Forms Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/aca-forms-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        ACA Forms Penalties
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1095-c-codes/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        ACA Forms Codes
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1095-b-vs-1095-c/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1095-B and 1095-C
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/aca-forms-bulk-upload/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        ACA Forms Bulk Upload
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1095-c-mailing-address/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1095-c Mailing address
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/other-icon.svg"
                      loading="lazy"
                      alt="Penalties, Credits & Amendments"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Other Forms
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/information-returns/form-1097-btc-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1097-BTC Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-3921-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3921 Instructions
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/resources/information-returns/form-3922-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3922 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1097-btc-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1097-BTC Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-3921-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3921 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-3922-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 3922 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1042-s-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 1042-S Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Information Returns End */}
          {/* Excise Tax Forms Start */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              //  minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#e7f8ed',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="excise"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/excise-icon.webp" />
                    <source srcSet="/assets/images/excise-icon.png" />
                    <img
                      src="/assets/images/excise-icon.png"
                      alt="Excise Tax Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Excise Tax</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    2290, 8849, 2290 Amendments
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="Getting Started with Form 2290 "
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Getting Started with Form 2290
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-schedule-1/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Schedule 1
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Gross Weight & Categories
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Taxable & Suspended Vehicles
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/new-york-hut/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        New York HUT
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/kentucky-form-2290/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Kentucky Form 2290
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/kansas-form-2290/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Kansas Form 2290
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/unified-carrier-registration/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Unified Carrier Registration (UCR)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/difference-between-irp-vs-ifta/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        IRP vs. IFTA
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/international-fuel-tax-agreement/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        International Fuel Tax Agreement (IFTA)
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/schedule-icon.svg"
                      loading="lazy"
                      alt="Form 941 Schedules"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Payment Options & Mobile App
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    & Amendments
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-irs-payment-options/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Payment Options
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/filing-form-2290-through-the-mobile-app/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 using Mobile App
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/penalties-icon.svg"
                      loading="lazy"
                      alt="Penalties, Credits & Amendments"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Penalties, Credits & Amendments
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/excise-tax/hvut-form-2290-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        HVUT Form 2290 Penalties
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-credit-vehicles/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Credit Vehicles
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-amendments/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 2290 Amendments
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/form-concepts.svg"
                      loading="lazy"
                      alt="Guides for Filers"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Guides for Filers
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-filing-guide-for-owner-operators/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Owner-Operator Guide
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Fleet Managers
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/automagic2290/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        AutoMagic2290
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/excise-tax/irp/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        International Registration Plan (IRP)
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Excise Tax Forms End */}
          {/* Extension Forms Start */}
          <Box
            className="card"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#e6eaf7',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="extension"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/extension-icon.webp" />
                    <source srcSet="/assets/images/extension-icon.png" />
                    <img
                      src="/assets/images/extension-icon.png"
                      alt="Extension Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Extension</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    7004, 4868, 8868, 8809
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/get-started-icon.svg"
                  loading="lazy"
                  alt="Business Tax Extension "
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Business Tax Extension
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        Form 7004
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-bulk-upload/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 Bulk Upload
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-for-form-1042/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 for 1042
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-for-form-1065/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 for 1065
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-for-form-1120/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 for 1120
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-form-codes/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 7004 Form Codes
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/tools/form-7004-due-date-calculator/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Tool - Form 7004 Due Date Calculator
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/schedule-icon.svg"
                  loading="lazy"
                  alt="Form 941 Schedules"
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                        }}
                      >
                        Individual Tax Extension
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        Form 4868
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-extension/form-4868-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4868 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-4868-bulk-upload/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4868 Bulk Upload
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-4868-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4868 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 4868 Mailing Address
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-7004-and-4868-comparison/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Compare: Form 7004 vs. Form 4868
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      m: 'auto',
                      mt: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Tax-Exempt Organization Extension
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        Form 8868
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-extension/form-8868-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8868 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-8868-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8868 Due Dates
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                  component={'img'}
                  src="/assets/images/penalties-icon.svg"
                  loading="lazy"
                  alt="Tax-Exempt Organization Extension"
                /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Information Return Extension
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontWeight: 400 }}
                      >
                        Form 8809
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/tax-extension/form-8809-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8809 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-8809-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8809 Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/tax-extension/form-15397-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 15397 Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Extension Forms End */}

          {/* Essential Forms Start */}
          <Box
            className="card"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#e5e4f7',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="essential"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: { xs: 'inherit', sm: 60 },
                    minHeight: { xs: 'inherit', sm: 60 },
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    p: 0,
                    width: 60,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/other-icon.webp" />
                    <source srcSet="/assets/images/other-icon.png" />
                    <img
                      src="/assets/images/other-icon.png"
                      alt="Essential Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    <span className="highlight-container">
                      <span className="highlight">Essential</span>
                    </span>{' '}
                    Forms
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontWeight: 500, color: '#637381' }}
                  >
                    W-9, W-8BEN, 8655, and BOI Report
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                      component="img"
                      src="/assets/images/get-started-icon.svg"
                      loading="lazy"
                      alt="Taxpayer Forms & Instructions"
                    /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        Taxpayer Forms & Instructions
                      </Typography>
                      {/* <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {' '}
                    (Quarterly Form)
                  </Typography> */}
                    </Box>
                  </Box>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/essential-forms/form-w-9-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form W-9 Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/essential-forms/form-8655-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Form 8655 Instructions
                      </Link>
                    </li>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      columnGap: 1,
                      margin: 'auto',
                      mt: 1,
                    }}
                  >
                    {/* <Box
                        component="img"
                        src="/assets/images/form-concepts.svg"
                        loading="lazy"
                        alt="Form Boir Series"
                      /> */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { md: 18, sm: 18, xs: 17 },
                          mt: 0,
                        }}
                      >
                        FinCEN BOI Reporting
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/fincen-boir/boi-reporting-instructions/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        BOI Reporting Instructions
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/fincen-boir/boi-reporting-due-dates/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        BOI Reporting Due Dates
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/fincen-boir/company-applicant-vs-beneficial-owner/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Company Applicant VS Beneficial Owners
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/fincen-boir/boir-penalties/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        BOI Report: Penalties
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box
            className="card"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
              // position: { xs: 'inherit', lg: 'sticky' },
              // top: { md: 90, lg: 90, xl: 90 },
              // minHeight: { md: '50vh', lg: '60vh', xl: '70vh' },
              // minHeight: 685,
              borderRadius: 6,
              px: { xs: 2, sm: 3, md: 5 },
              py: 3,
              backgroundColor: '#e6e6e8',
              border: '10px solid #fff',
              scrollMarginTop: 140,
            }}
            id="payment"
          >
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={2}
                sx={{
                  mr: 0,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  mb: 0,
                }}
              >
                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 20, sm: 20, md: 22, lg: 24 },
                      fontWeight: 600,
                      mb: 0,
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    Tax{' '}
                    <span className="highlight-container">
                      <span className="highlight">Payment</span>
                    </span>
                  </Typography>
                </Box>
              </Stack>
              <Grid
                container
                spacing={2}
                rowSpacing={0}
                m={0}
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12 }}>
                  <Box component="ul">
                    <li>
                      <Link
                        href="/resources/eftps/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        EFTPS Payment Method
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/electronic-funds-withdrawal-efw/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        Electronic Fund Withdrawal (EFW)
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/resources/pay-taxes-by-efw/"
                        sx={{
                          textDecoration: 'none',
                          color: '#333',
                          '&:hover': { color: '#0202ff' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/images/form-icon.svg"
                          loading="lazy"
                          sx={{ mr: 1, mb: -0.5 }}
                        />
                        EFW Payment Timeline
                      </Link>
                    </li>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Resources;
